import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { lazy, Suspense } from "react";
import api from "../../services/api";
import { langoptions } from "../../utils/constants";
import { Toast } from "../../shared/Toast";

const EditAdModal = lazy(() => import("./EditAdModal"));

const NewspaperViewer = () => {
  const [selectedPage, setSelectedPage] = useState(0);
  const [clickCoordinates, setClickCoordinates] = useState(null);
  const [newspapers, setNewspapers] = useState([]);
  const [selectedNewspaper, setSelectedNewspaper] = useState(null);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [editions, setEditions] = useState([]);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState({ label: "Telugu", value: 1 });
  const [selectedPageName, setSelectedPageName] = useState({ value: "main", label: "Main Edition" });
  const [newspaperData, setNewspaperData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [originalUrls, setOriginalUrls] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [pageNames, setPageNames] = useState([]);

  const imageRef = useRef(null);
  const showStateDistrictFilters = selectedPageName?.value !== "main";

  useEffect(() => {
    const fetchNewspapers = async () => {
      try {
        const res = await api({ url: "/market/newspapers", method: "POST", data: {} });
        if (res?.code === 200) {
          const formattedNewspapers =
            res?.data
              ?.filter((item) => item?.name?.toLowerCase() !== "way2news")
              ?.map((item) => ({
                value: item?.name?.toLowerCase() === "dinathanthi" ? "dailythanthi" : item?.id,
                label: item?.name?.toLowerCase() === "dinathanthi" ? "Dailythanthi" : item?.name,
                logo: item?.logo,
                ...item,
              })) || [];
          setNewspapers(formattedNewspapers);
          const eenaduNewspaper = formattedNewspapers?.find((paper) => paper?.label?.toLowerCase() === "eenadu");
          if (eenaduNewspaper) setSelectedNewspaper(eenaduNewspaper);
        }
      } catch (error) {
        console.error("Error fetching newspapers:", error);
      }
    };
    fetchNewspapers();
  }, []);

  useEffect(() => {
    const fetchPageNames = async () => {
      try {
        const res = await api({ url: "/birdview/getPageNames", method: "POST", data: { lang_id: selectedLanguage?.value } });
        if (res?.code === 200) {
          const formattedPageNames =
            res.data?.map((page) => ({
              value: page?.value ?? page?.name,
              label: page?.label ?? page?.name,
            })) || [];
          setPageNames(formattedPageNames);
          setSelectedPageName(formattedPageNames.find((p) => p.value === "main") || formattedPageNames[0] || null);
        }
      } catch (error) {
        console.error("Error fetching page names:", error);
      }
    };
    if (selectedLanguage?.value) {
      fetchPageNames();
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedDate && selectedLanguage?.value && selectedPageName?.value && (!showStateDistrictFilters || (selectedState?.label && selectedDistrict?.label))) {
      fetchEditions();
    } else {
      setEditions([]);
      setSelectedEdition(null);
    }
  }, [selectedDate, selectedLanguage, selectedPageName, selectedState, selectedDistrict]);

  useEffect(() => {
    if (selectedDate && selectedLanguage?.value && selectedPageName?.value && showStateDistrictFilters) {
      fetchStates();
    } else {
      setStates([]);
      setSelectedState(null);
      setDistricts([]);
      setSelectedDistrict(null);
    }
  }, [selectedDate, selectedLanguage, selectedPageName]);

  useEffect(() => {
    if (selectedState?.label && showStateDistrictFilters) {
      fetchDistricts();
    } else {
      setDistricts([]);
      setSelectedDistrict(null);
    }
    setSelectedDistrict(null);
  }, [selectedState]);

  useEffect(() => {
    if (selectedNewspaper?.value && selectedPageName?.value && selectedEdition?.value && selectedLanguage?.value) {
      fetchNewspaperData();
    } else {
      setNewspaperData([]);
    }
  }, [selectedNewspaper, selectedPageName, selectedEdition, selectedState, selectedDistrict, selectedDate, selectedLanguage]);

  const fetchEditions = async () => {
    try {
      const response = await api({
        url: "/birdview/epaper_editions",
        method: "POST",
        data: {
          date: selectedDate,
          language: selectedLanguage?.value,
          category: selectedPageName?.value,
          state: showStateDistrictFilters ? selectedState?.label : null,
          district: showStateDistrictFilters ? selectedDistrict?.label : null,
          newspaper: selectedNewspaper?.value,
        },
      });
      const editionsData = Array.isArray(response?.data?.result) ? response.data.result : Array.isArray(response?.data) ? response.data : [];
      const formattedEditions = editionsData.map((e) => ({
        value: e?.value ?? e?.label,
        label: e?.label ?? e?.value,
      }));
      setEditions(formattedEditions);
      setSelectedEdition(formattedEditions.find((e) => e.value === "ts") || formattedEditions[0] || null);
    } catch (error) {
      console.error("Error fetching editions:", error);
      setEditions([]);
      setSelectedEdition(null);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api({
        url: "/birdview/epaper_states",
        method: "POST",
        data: {
          date: selectedDate,
          language: selectedLanguage?.value,
          category: selectedPageName?.value,
          newspaper: selectedNewspaper.value,
        },
      });
      const stateOptions = (Array.isArray(response?.data?.result) ? response.data.result : Array.isArray(response?.data) ? response.data : []).map((state) => ({
        value: state?.value ?? state?.label,
        label: state?.label ?? state?.value,
      }));
      setStates(stateOptions);
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
      setSelectedState(null);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api({
        url: "/birdview/epaper_districts",
        method: "POST",
        data: {
          date: selectedDate,
          language: selectedLanguage?.value,
          category: selectedPageName?.value,
          state: selectedState?.label,
          newspaper: selectedNewspaper.value,
        },
      });
      const districtOptions = (Array.isArray(response?.data?.result) ? response.data.result : Array.isArray(response?.data) ? response.data : []).map((district) => ({
        value: district?.value ?? district?.label,
        label: district?.label ?? district?.value,
      }));
      setDistricts(districtOptions);
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
      setSelectedDistrict(null);
    }
  };

  const fetchNewspaperData = async () => {
    try {
      setLoading(true);
      const payload = {
        newspaper_id: selectedNewspaper?.value,
        newspaper_name: selectedNewspaper?.label,
        page_name: selectedPageName?.value,
        edition: selectedEdition?.value,
        state: showStateDistrictFilters ? selectedState?.label : null,
        district: showStateDistrictFilters ? selectedDistrict?.label : null,
        date: selectedDate,
        lang_id: selectedLanguage?.value,
      };
      const response = await api({ url: "/birdview/newspaper-data", method: "POST", data: payload });
      if (response?.data) {
        setNewspaperData(response.data);
        setSelectedPage(0);
      } else {
        setNewspaperData([]);
      }
    } catch (error) {
      console.error("Error fetching newspaper data:", error);
      setNewspaperData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewspaperChange = (selected) => {
    setSelectedNewspaper(selected);
    setSelectedPageName({ value: "main", label: "Main Edition" });
    setSelectedEdition(null);
    setSelectedState(null);
    setSelectedDistrict(null);
    setNewspaperData([]);
  };

  const handlePageNameChange = (selected) => {
    setSelectedPageName(selected);
    setSelectedEdition(null);
    setSelectedState(null);
    setSelectedDistrict(null);
    setNewspaperData([]);
  };

  const handleLanguageChange = (selected) => {
    setSelectedLanguage(selected);
    setSelectedPageName({ value: "main", label: "Main Edition" });
    setSelectedEdition(null);
    setSelectedState(null);
    setSelectedDistrict(null);
    setNewspaperData([]);
  };

  const handleImageClick = async (e) => {
    const image = imageRef.current;
    if (!image) return;
    const rect = image.getBoundingClientRect();
    const scaleX = image.naturalWidth / image.clientWidth;
    const scaleY = image.naturalHeight / image.clientHeight;
    const originalX = Math.round((e.clientX - rect.left) * scaleX);
    const originalY = Math.round((e.clientY - rect.top) * scaleY);
    setClickCoordinates({
      original: { x: originalX, y: originalY },
      displayed: { x: Math.round(e.clientX - rect.left), y: Math.round(e.clientY - rect.top) },
    });
    const payload = {
      x: originalX,
      y: originalY,
      page_data: newspaperData[selectedPage] || {},
      newspaper_id: selectedNewspaper?.value,
      newspaper_name: selectedNewspaper?.label,
      page_name: selectedPageName?.value,
      edition: selectedEdition?.value,
      state: showStateDistrictFilters ? selectedState?.label : null,
      district: showStateDistrictFilters ? selectedDistrict?.label : null,
      date: selectedDate,
      page_num: newspaperData[selectedPage]?.page_num,
    };
    try {
      const response = await api({ url: "/birdview/cropped-image", method: "POST", data: payload });
      if (response.status === "SUCCESS") {
        if (response.data) {
          setCroppedImage(response.data);
          setOriginalUrls(Array.isArray(response.data.original_url) ? response.data.original_url : [response.data.original_url]);
          setShowModal(true);
        } else {
          Toast("info", "NO AD FOUND");
          setCroppedImage(null);
          setOriginalUrls([]);
          setShowModal(true);
        }
      } else {
        Toast("error", "Failed To Get Cropped Image");
      }
    } catch (error) {
      console.error("Error fetching cropped image:", error);
      setCroppedImage(null);
      setOriginalUrls([]);
      setShowModal(true);
    }
  };

  const scrollThumbnails = (direction) => {
    const container = document.getElementById("thumbnails-container");
    container?.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {selectedNewspaper?.logo && <img src={selectedNewspaper.logo} alt={selectedNewspaper?.label || "Newspaper"} className="h-12 object-contain" />}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center flex-1">
              <div className="w-48">
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="w-48">
                <Select value={selectedNewspaper} onChange={handleNewspaperChange} options={newspapers} placeholder="Select Newspaper" className="text-sm" isDisabled={newspapers.length === 0} />
              </div>
              <div className="w-48">
                <Select value={selectedLanguage} onChange={handleLanguageChange} options={langoptions} placeholder="Select Language" className="text-sm" />
              </div>
              <div className="w-48">
                <Select value={selectedPageName} onChange={handlePageNameChange} options={pageNames} placeholder="Select Page Type" className="text-sm" isDisabled={!selectedNewspaper || pageNames.length === 0} />
              </div>
              {showStateDistrictFilters && (
                <>
                  <div className="w-48">
                    <Select value={selectedState} onChange={setSelectedState} options={states} placeholder="Select State" className="text-sm" isDisabled={!selectedLanguage || !selectedPageName || states.length === 0} />
                  </div>
                  <div className="w-48">
                    <Select value={selectedDistrict} onChange={setSelectedDistrict} options={districts} placeholder="Select District" className="text-sm" isDisabled={!selectedState || districts.length === 0} />
                  </div>
                </>
              )}
              <div className="w-48">
                <Select
                  value={selectedEdition}
                  onChange={setSelectedEdition}
                  options={editions}
                  placeholder="Select Edition"
                  className="text-sm"
                  isDisabled={!selectedLanguage || !selectedPageName || (showStateDistrictFilters && (!selectedState || !selectedDistrict)) || editions.length === 0}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading && (
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-2 text-center text-sm text-gray-600">Loading newspaper data...</div>
        </div>
      )}
      {newspaperData?.length > 0 && (
        <>
          <div className="px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b flex justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Page {newspaperData[selectedPage]?.page_num ? String(newspaperData[selectedPage].page_num) : "00"}</h2>
                <div className="flex space-x-2">
                  <button onClick={() => setSelectedPage(Math.max(0, selectedPage - 1))} disabled={selectedPage === 0} className="p-2 disabled:opacity-50">
                    ◀
                  </button>
                  <span>
                    {selectedPage + 1} / {newspaperData.length}
                  </span>
                  <button onClick={() => setSelectedPage(Math.min(newspaperData.length - 1, selectedPage + 1))} disabled={selectedPage === newspaperData.length - 1} className="p-2 disabled:opacity-50">
                    ▶
                  </button>
                </div>
              </div>
              <div className="p-6 flex flex-col lg:flex-row justify-between gap-6">
                <div className="relative flex-1">
                  <img loading="lazy" ref={imageRef} src={newspaperData[selectedPage]?.link_url} alt={`Page ${selectedPage + 1}`} onClick={handleImageClick} className="max-w-full h-auto shadow-lg rounded-lg cursor-crosshair" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {showModal && croppedImage && (
        <Suspense fallback={null}>
        <EditAdModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setCroppedImage(null);
            setOriginalUrls([]);
            setClickCoordinates(null);
          }}
          croppedImage={croppedImage}
          originalUrls={originalUrls}
          prefillData={{
            date: selectedDate,
            language: selectedLanguage,
            newspaper: selectedNewspaper,
            page_name: selectedPageName?.value,
            edition: selectedEdition,
            state: showStateDistrictFilters ? selectedState : null,
            district: showStateDistrictFilters ? selectedDistrict : null,
            page_num: newspaperData[selectedPage]?.page_num,
          }}
        />
        </Suspense>
      )}
    </div>
  );
};

export default NewspaperViewer;
